/* <sst-product>: variant picking, add to bag (AJAX), size guide link,
   video review dialog and the sticky add-to-bag bar. */
if (!customElements.get('sst-product')) {
  customElements.define(
    'sst-product',
    class SstProduct extends HTMLElement {
      static ADDED_MS = 2000;

      connectedCallback() {
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
        this.form = this.querySelector('form[id^="sst-product-form"]');
        this.addBtn = this.querySelector('[data-add]');
        this.bar = this.querySelector('[data-bar]');
        this.money = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency });

        this.addEventListener('change', (e) => this.onChange(e));
        this.addEventListener('click', (e) => this.onClick(e));
        this.form.addEventListener('submit', (e) => this.onSubmit(e));

        this.videoList = this.querySelector('[data-video-list]');
        this.videoControls = this.querySelector('[data-video-controls]');
        if (this.videoList && this.videoControls) {
          this.videoScrollHandler = () => requestAnimationFrame(() => this.updateVideoNav());
          this.videoList.addEventListener('scroll', this.videoScrollHandler, { passive: true });
          this.videoResizeObserver = new ResizeObserver(() => this.updateVideoNav());
          this.videoResizeObserver.observe(this.videoList);
          requestAnimationFrame(() => this.updateVideoNav());
        }

        if (this.bar) {
          /* Root extended far below the fold: the buy row only "leaves" once it scrolls up past the top. */
          this.observer = new IntersectionObserver(
            ([entry]) => {
              this.bar.classList.toggle('is-visible', !entry.isIntersecting);
              this.bar.inert = entry.isIntersecting;
            },
            { rootMargin: '0px 0px 100000px 0px' }
          );
          this.observer.observe(this.querySelector('[data-buy-row]'));
        }

        this.update();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.videoResizeObserver?.disconnect();
        this.videoList?.removeEventListener('scroll', this.videoScrollHandler);
      }

      /* Selected value per option, null where nothing is picked yet. */
      selected() {
        return [...this.querySelectorAll('[data-option]')].map((fs) => fs.querySelector(':checked')?.value ?? null);
      }

      matches(variant, opts) {
        return opts.every((o, i) => o === null || variant.options[i] === o);
      }

      update(fromUser = false) {
        const opts = this.selected();
        const complete = !opts.includes(null);
        const variant = complete
          ? this.variants.find((v) => this.matches(v, opts))
          : this.variants.find((v) => v.available && this.matches(v, opts)) || this.variants.find((v) => this.matches(v, opts));
        this.variant = complete ? variant : null;

        /* Grey out values that have no available variant with the other picks. */
        this.querySelectorAll('[data-option]').forEach((fs, i) => {
          fs.querySelectorAll('[data-option-input]').forEach((input) => {
            const test = opts.map((o, j) => (j === i ? input.value : o));
            const ok = this.variants.some((v) => v.available && this.matches(v, test));
            input.closest('label').classList.toggle('is-unavailable', !ok);
          });
          const value = fs.querySelector('[data-option-value]');
          if (value) value.textContent = opts[i] || '';
        });

        if (!variant) {
          this.addBtn.disabled = true;
          this.addBtn.textContent = this.dataset.labelUnavailable;
          return;
        }

        this.querySelector('[data-variant-id]').value = variant.id;
        this.querySelector('[data-price]').textContent = this.money.format(variant.price / 100);
        this.addBtn.disabled = !variant.available;
        this.setAddLabel(variant);
        if (complete) this.querySelectorAll('[data-option-error]').forEach((el) => (el.hidden = true));
        this.updateBarMeta(opts);

        if (complete && fromUser) {
          const url = new URL(location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState(null, '', url);
        }
        if (fromUser) this.showMedia(variant.media);
      }

      setAddLabel(variant = this.variant) {
        if (!variant) return;
        if (!variant.available) {
          this.addBtn.textContent = this.dataset.labelSoldOut;
          return;
        }
        const qty = Math.max(1, parseInt(this.form.quantity.value, 10) || 1);
        this.addBtn.textContent = `${this.dataset.labelAdd} · ${this.money.format((variant.price * qty) / 100)}`;
      }

      updateBarMeta(opts) {
        const meta = this.querySelector('[data-bar-meta]');
        if (meta) meta.textContent = [this.querySelector('[data-price]').textContent, ...opts.filter(Boolean)].join(' · ');
      }

      /* Scroll the gallery to the variant's image; the order never changes. */
      showMedia(mediaId) {
        const media = mediaId && this.querySelector(`[data-gallery] [data-media-id="${mediaId}"]`);
        if (!media) return;
        const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
        media.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'nearest', inline: 'start' });
      }

      onChange(e) {
        const t = e.target;
        if (t.matches('[data-option-input]')) return this.update(true);
        if (t.matches('[data-qty-input]')) return this.setAddLabel();
      }

      onClick(e) {
        const btn = e.target.closest('button');
        if (!btn) return;
        if (btn.dataset.qty) {
          const input = this.form.quantity;
          input.value = Math.min(99, Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.qty)));
          return this.setAddLabel();
        }
        if (btn.matches('[data-size-guide]')) {
          const target = this.querySelector('[data-size-guide-target]');
          if (!target) return;
          this.querySelectorAll('.sst-product__acc[open]').forEach((acc) => acc !== target && (acc.open = false));
          target.open = true;
          target.querySelector('summary').focus({ preventScroll: true });
          /* Next frame, so the closed accordions above have collapsed before measuring. */
          return requestAnimationFrame(() => this.scrollToAccordion(target));
        }
        if (btn.matches('[data-video-review]')) return this.playVideo(btn);
        if (btn.matches('[data-video-nav]')) return this.scrollVideos(Number(btn.dataset.videoNav));
        if (btn.matches('[data-dialog-close]')) return btn.closest('dialog').close();
      }

      updateVideoNav() {
        if (!this.videoList || !this.videoControls) return;
        const maxScroll = this.videoList.scrollWidth - this.videoList.clientWidth;
        this.videoControls.hidden = maxScroll <= 1;
        const previous = this.videoControls.querySelector('[data-video-nav="-1"]');
        const next = this.videoControls.querySelector('[data-video-nav="1"]');
        previous.disabled = this.videoList.scrollLeft <= 1;
        next.disabled = this.videoList.scrollLeft >= maxScroll - 1;
      }

      scrollVideos(direction) {
        const item = this.videoList?.querySelector('li');
        if (!item) return;
        const gap = parseFloat(getComputedStyle(this.videoList).columnGap) || 0;
        const distance = item.getBoundingClientRect().width + gap;
        const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.videoList.scrollBy({ left: direction * distance, behavior: smooth ? 'smooth' : 'auto' });
      }

      /* The buy column is sticky, so scrolling only moves the accordion once the column unpins.
         Jump instantly until it settles to find the end position, then go back and scroll there once. */
      scrollToAccordion(target) {
        const start = scrollY;
        const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
        for (let i = 0; i < 10; i++) {
          const before = scrollY;
          window.scrollTo({ top: before + target.getBoundingClientRect().top - margin, behavior: 'instant' });
          if (Math.abs(scrollY - before) < 1) break;
        }
        const end = scrollY;
        window.scrollTo({ top: start, behavior: 'instant' });
        const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: end, behavior: smooth ? 'smooth' : 'instant' });
      }

      async onSubmit(e) {
        e.preventDefault();
        const missing = [...this.querySelectorAll('[data-option]')].filter((fs) => !fs.querySelector(':checked'));
        if (missing.length) {
          missing.forEach((fs) => (fs.querySelector('[data-option-error]').hidden = false));
          return missing[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        if (!this.variant?.available) return;

        const error = this.querySelector('[data-form-error]');
        error.hidden = true;
        this.addBtn.disabled = true;
        try {
          /* FormData (multipart) so file inputs from apps travel as line item properties. */
          const body = new FormData(this.form);
          const drawer = document.querySelector('cart-drawer');
          if (drawer) {
            body.append('sections', 'cart-drawer,cart-icon-bubble');
            body.append('sections_url', window.location.pathname);
          }
          const res = await fetch(window.routes?.cart_add_url || '/cart/add', {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body,
          });
          const data = await res.json();
          if (!res.ok || data.status) throw new Error(data.description || data.message);
          drawer?.classList.remove('is-empty');
          drawer?.renderContents(data);
          SST.refreshCartCount();
          this.flashAdded();
        } catch (err) {
          error.textContent = err.message || window.cartStrings?.error || '';
          error.hidden = false;
        } finally {
          this.addBtn.disabled = !this.variant?.available;
        }
      }

      flashAdded() {
        const buttons = [this.addBtn, this.querySelector('[data-bar-add]')].filter(Boolean);
        const labels = buttons.map((b) => b.textContent);
        buttons.forEach((b) => (b.textContent = `${this.dataset.labelAdded} ✓`));
        clearTimeout(this.timer);
        this.timer = setTimeout(() => buttons.forEach((b, i) => (b.textContent = labels[i])), SstProduct.ADDED_MS);
      }

      /* Video reviews: the block's <template> holds the video; it's only loaded on click. */
      playVideo(btn) {
        const dialog = this.querySelector('[data-video-dialog]');
        const template = btn.parentElement.querySelector('template');
        if (!dialog || !template) return;
        const body = dialog.querySelector('[data-dialog-body]');
        body.replaceChildren(template.content.cloneNode(true));
        dialog.addEventListener('close', () => body.replaceChildren(), { once: true });
        dialog.showModal();
      }
    }
  );
}
